import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useWanName, WanLabel } from "./WanNames";
import useSortable from "../hooks/useSortable";
import useVisibleInterval from "../hooks/useVisibleInterval";
import useWheel from "../hooks/useWheel";
import { fetchWithTimeout } from "../utils/fetchWithTimeout";
import {
  FiShield, FiZap, FiMousePointer, FiFilm, FiDatabase,
  FiClock, FiGlobe, FiActivity, FiPlus, FiX, FiWifi, FiCheck,
  FiAlertCircle, FiPackage, FiTv, FiPlay, FiRadio,
  FiSearch, FiLink, FiArrowUp, FiArrowDown, FiInfo,
  FiLayers, FiChevronDown, FiChevronUp, FiAlertTriangle,
} from "react-icons/fi";
import {
  SiZoom, SiWhatsapp, SiSignal,
  SiDiscord, SiNetflix, SiYoutube, SiTwitch, SiSpotify,
  SiTiktok, SiSlack,
  SiTelegram, SiFacebook, SiInstagram, SiLinkedin, SiSnapchat,
  SiGithub, SiApple, SiDropbox, SiSteam
} from "react-icons/si";
import { BsMicrosoftTeams, BsGoogle } from "react-icons/bs";

/* ═══════════════════════════════════════════════════════════════════════════
   ICON + COLOR MAPS
   ═══════════════════════════════════════════════════════════════════════════ */

const APP_ICONS = {
  APP_ZOOM:          { Icon: SiZoom,           color: "#2D8CFF" },
  APP_GOOGLE_MEET:   { Icon: BsGoogle,         color: "#00897B" },
  APP_MSTEAMS:       { Icon: BsMicrosoftTeams, color: "#6264A7" },
  APP_WHATSAPP_CALL: { Icon: SiWhatsapp,       color: "#25D366" },
  APP_SIGNAL:        { Icon: SiSignal,         color: "#3A76F0" },
  APP_DISCORD:       { Icon: SiDiscord,        color: "#5865F2" },
  APP_NETFLIX:       { Icon: SiNetflix,         color: "#E50914" },
  APP_YOUTUBE:       { Icon: SiYoutube,        color: "#FF0000" },
  APP_TWITCH:        { Icon: SiTwitch,         color: "#9146FF" },
  APP_SPOTIFY:       { Icon: SiSpotify,        color: "#1DB954" },
  APP_TIKTOK:        { Icon: SiTiktok,         color: "#FE2C55" },
  APP_AMAZON_VIDEO:  { Icon: FiTv,             color: "#00A8E1" },
  APP_DISNEYPLUS:    { Icon: FiPlay,           color: "#113CCF" },
  APP_HULU:          { Icon: FiRadio,          color: "#1CE783" },
  APP_HBO:           { Icon: FiFilm,           color: "#991EEB" },
  APP_SLACK:         { Icon: SiSlack,          color: "#4A154B" },
  APP_TELEGRAM:      { Icon: SiTelegram,       color: "#0088CC" },
  APP_FACEBOOK:      { Icon: SiFacebook,       color: "#1877F2" },
  APP_INSTAGRAM:     { Icon: SiInstagram,      color: "#E4405F" },
  APP_LINKEDIN:      { Icon: SiLinkedin,       color: "#0A66C2" },
  // Brand colours where they read on white; a brand that is neon, grey or
  // black gets a hue of its own instead, since grey reads as switched off.
  APP_SNAPCHAT:      { Icon: SiSnapchat,       color: "#E5B000" },
  APP_GITHUB:        { Icon: SiGithub,         color: "#8250DF" },
  APP_APPLE:         { Icon: SiApple,          color: "#0071E3" },
  APP_OOKLA:         { Icon: FiZap,            color: "#0891B2" },
  APP_DROPBOX:       { Icon: SiDropbox,        color: "#0061FF" },
  APP_STEAM:         { Icon: SiSteam,          color: "#1A9FFF" },
};

const CATEGORY_ICONS = {
  CRITICAL: FiShield, REALTIME: FiZap, INTERACTIVE: FiMousePointer,
  STREAMING: FiFilm, BULK: FiDatabase, BACKGROUND: FiClock,
  BE: FiGlobe, DIAG: FiActivity,
};

// One distinct hue per category. None is grey: a grey orb reads as switched off.
const CATEGORY_COLORS = {
  CRITICAL: "#EF4444", REALTIME: "#F59E0B", INTERACTIVE: "#6366F1",
  STREAMING: "#EC4899", BULK: "#8B5CF6", BACKGROUND: "#0D9488",
  BE: "#2563EB", DIAG: "#0891B2",
};

/* helpers */
function formatCount(n) {
  if (n >= 1e9) return (n / 1e9).toFixed(1) + "B";
  if (n >= 1e6) return (n / 1e6).toFixed(1) + "M";
  if (n >= 1e3) return (n / 1e3).toFixed(1) + "K";
  return String(n);
}

// The order a list takes when the row at `from` is moved to `to`.
const moved = (list, from, to) => {
  const next = list.slice();
  const [it] = next.splice(from, 1);
  next.splice(to, 0, it);
  return next;
};

function classAccent(item) {
  return item.isApp
    ? APP_ICONS[item.key]?.color || "#8B5CF6"
    : CATEGORY_COLORS[item.key] || "#6366F1";
}

function ClassIcon({ item, size = 14 }) {
  if (item.isApp) {
    const Ic = APP_ICONS[item.key]?.Icon;
    return Ic ? <Ic size={size} /> : <FiPackage size={size} />;
  }
  const Ic = CATEGORY_ICONS[item.key] || FiGlobe;
  return <Ic size={size} />;
}

/* ═══════════════════════════════════════════════════════════════════════════
   INLINE PRIORITY EDITOR — the routing order for ONE selected class
   ═══════════════════════════════════════════════════════════════════════════ */

/* What the line under the editor says about the last change: nothing to press,
   since every change is applied as it is made. */
function SaveLine({ save }) {
  if (save?.state === "saving") return <div className="shp-save" role="status"><span className="shp-save-spin" /> Saving…</div>;
  if (save?.state === "saved") return <div className="shp-save shp-save--ok" role="status"><FiCheck size={13} /> Saved, in use now</div>;
  if (save?.state === "failed") {
    const why = /[.!?]$/.test(save.error) ? save.error : `${save.error}.`;
    return (
      <div className="shp-save shp-save--err" role="alert">
        <FiAlertCircle size={13} /> That change did not apply: {why} The list shows what the box has.
      </div>
    );
  }
  return <div className="shp-save" role="status">Changes take effect as you make them.</div>;
}

function PriorityEditor({
  accentColor, selectedClass, selectedNode, loading,
  interfaces, availableWans, selectedDesired,
  currentByName, activeWan,
  applyOrder, orderSave,
  isInheritingPriority, inheritedFromCategory, clearAppOverride,
  bonded, setClassMode, modeBusy,
}) {
  const isSpread = bonded;
  const saving = orderSave?.state === "saving";
  // Order is meaningless while the class bonds, so its list does not sort.
  const sort = useSortable((from, to) => applyOrder(moved(selectedDesired, from, to)), { disabled: isSpread });

  if (!selectedNode) {
    return (
      <div className="shp-edit-empty">
        <FiAlertCircle size={18} /> Select an edge node first to edit routing.
      </div>
    );
  }

  const move = (from, to) => {
    if (to < 0 || to >= selectedDesired.length) return;
    applyOrder(moved(selectedDesired, from, to));
  };
  const last = selectedDesired.length === 1;

  return (
    <div className="shp-edit" style={{ "--shp-accent": accentColor }}>
      {/* Mode picker — how this class uses the connections below */}
      {setClassMode && (
        <div className="shp-mode">
          <div className="shp-mode-seg" role="group" aria-label="Routing mode">
            <button
              type="button"
              className={`shp-mode-btn${isSpread ? " shp-mode-btn--on" : ""}`}
              onClick={() => setClassMode(selectedClass.key, true)}
              disabled={modeBusy || isSpread}
              title="Bond this class's flows across all the connections below at once. Order is ignored; a single flow still rides one link."
            >
              <FiLayers size={13} /> Spread across these
            </button>
            <button
              type="button"
              className={`shp-mode-btn${!isSpread ? " shp-mode-btn--on" : ""}`}
              onClick={() => setClassMode(selectedClass.key, false)}
              disabled={modeBusy || !isSpread}
              title="Use the connections in the order below — first one that's online carries the traffic (failover)."
            >
              <FiArrowDown size={13} /> Use in this order
            </button>
          </div>
          <span className="shp-mode-hint">
            {isSpread
              ? "Spreading — flows are shared across every connection below; the ORDER doesn't matter, only which connections are listed."
              : "Failover — traffic uses the connections top-to-bottom; drag to set which comes first. Applying a QoS quality ceiling keeps a class here."}
          </span>
        </div>
      )}

      {/* Plain-language banner */}
      {isInheritingPriority ? (
        <div className="shp-note shp-note--inherit">
          <FiLink size={13} />
          <span>
            This app currently follows the <strong>{inheritedFromCategory}</strong> category
            order. Changing it below creates a routing rule just for {selectedClass.label}.
          </span>
        </div>
      ) : selectedClass.isApp ? (
        <div className="shp-note shp-note--custom">
          <FiInfo size={13} />
          <span>Custom order for {selectedClass.label} (overrides its {selectedClass.category} category).</span>
          <button className="shp-revert" type="button" onClick={clearAppOverride} disabled={saving}>
            <FiLink size={11} /> Follow {selectedClass.category} again
          </button>
        </div>
      ) : (
        <div className="shp-note">
          <FiInfo size={13} />
          <span>
            {isSpread
              ? `${selectedClass.label} traffic is spread across every connection listed — add or remove connections to change which links it bonds over.`
              : `Connections are tried top-to-bottom. ${selectedClass.label} traffic uses the first one that's online.`}
          </span>
        </div>
      )}

      <div className="shp-edit-grid">
        {/* Priority order */}
        <div className="shp-col">
          <div className="shp-col-head">
            <span className="shp-col-title">{isSpread ? "Bonded connections" : "Preferred connections"}</span>
            <span className="shp-col-hint">{isSpread ? "unordered — bonded together" : "top = first choice"}</span>
          </div>
          <div className={`shp-order${sort.dragging !== null ? " shp-order--sorting" : ""}`} ref={sort.listRef}>
            {selectedDesired.length === 0 && (
              <div className="shp-order-empty">
                No connections chosen yet — add one from the right.
              </div>
            )}
            {selectedDesired.map((wan, i) => {
              const st = currentByName?.[wan];
              const isActive = activeWan && wan === activeWan;
              const isDegraded = !!st?.degraded;
              const isOffline = st ? st.enabled === false : false;
              return (
                <div
                  key={wan}
                  {...sort.rowProps(i)}
                  className={`shp-rank${isSpread ? " shp-rank--fixed" : ""}${sort.dragging === i ? " shp-rank--drag" : ""}${isActive ? " shp-rank--active" : ""}`}
                >
                  {isSpread
                    ? <span className="shp-rank-dot" aria-hidden />
                    : <span className="shp-rank-num" data-sort-handle title="Drag to reorder">{i + 1}</span>}
                  <FiWifi size={14} className="shp-rank-ico" />
                  <span className="shp-rank-name"><WanLabel iface={wan} /></span>
                  {isSpread ? (
                    isOffline
                      ? <span className="shp-rank-tag shp-rank-tag--offline">offline</span>
                      : isDegraded
                        ? <span className="shp-rank-tag shp-rank-tag--degraded"><FiAlertCircle size={10} /> degraded · not in bond</span>
                        : <span className="shp-rank-tag shp-rank-tag--active"><FiCheck size={10} /> bonding</span>
                  ) : (
                    <>
                      {isActive && <span className="shp-rank-tag shp-rank-tag--active"><FiCheck size={10} /> carrying now</span>}
                      {!isActive && isDegraded && <span className="shp-rank-tag shp-rank-tag--degraded"><FiAlertCircle size={10} /> degraded</span>}
                      {!isActive && !isDegraded && isOffline && <span className="shp-rank-tag shp-rank-tag--offline">offline</span>}
                    </>
                  )}
                  {!isSpread && (
                    <div className="shp-rank-moves">
                      <button type="button" className="shp-move" title="Move up" disabled={i === 0} onClick={() => move(i, i - 1)}><FiArrowUp size={12} /></button>
                      <button type="button" className="shp-move" title="Move down" disabled={i === selectedDesired.length - 1} onClick={() => move(i, i + 1)}><FiArrowDown size={12} /></button>
                    </div>
                  )}
                  <button
                    type="button"
                    className={`shp-rank-rm${isSpread ? " shp-rank-rm--end" : ""}`}
                    title={last ? `${selectedClass.label} needs at least one connection` : "Remove"}
                    disabled={last}
                    onClick={() => applyOrder(selectedDesired.filter((w) => w !== wan))}
                  >
                    <FiX size={13} />
                  </button>
                </div>
              );
            })}
          </div>
          {last && (
            <div className="shp-col-note">
              {selectedClass.label} needs at least one connection, so add another before removing this one.
            </div>
          )}
        </div>

        {/* Available connections */}
        <div className="shp-col">
          <div className="shp-col-head">
            <span className="shp-col-title">Available connections</span>
          </div>
          <div className="shp-avail">
            {loading && interfaces.length === 0 && <div className="shp-order-empty">Loading…</div>}
            {!loading && availableWans.length === 0 && (
              <div className="shp-all-added"><FiCheck size={14} /> All connections added</div>
            )}
            {availableWans.map((wan) => {
              const isUp = wan.state === "up" || wan.state === "UP";
              return (
                <button key={wan.name} type="button" className="shp-avail-row" onClick={() => applyOrder([...selectedDesired, wan.name])}>
                  <span className={`shp-dot${isUp ? " shp-dot--up" : ""}`} />
                  <span className="shp-avail-name"><WanLabel iface={wan.name} /></span>
                  {wan.addresses?.[0] && <span className="shp-avail-addr">{wan.addresses[0]}</span>}
                  <FiPlus size={14} className="shp-avail-plus" />
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <SaveLine save={orderSave} />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   BULK ORDER EDITOR — set ONE baseline WAN order across every category
   ═══════════════════════════════════════════════════════════════════════════ */

function BulkOrderEditor({ interfaces, selectedNode, applyBulkOrder, bulkApplying, customizedCount, bondedCount, baselineOrder }) {
  const [open, setOpen] = useState(false);
  /* The baseline is not a draft. Once applied it is the order every category
     carries, so the editor opens on what the edge actually holds rather than on
     an empty list — leaving this panel and coming back used to look like the
     change had been thrown away. */
  const [order, setOrder] = useState(baselineOrder || []);
  const sort = useSortable((from, to) => setOrder((o) => moved(o, from, to)));

  /* Re-seed when the edge's own baseline changes: a different node, or an apply
     that just landed. Keyed on the content, so the five-second class poll
     re-rendering with the same order never overwrites an edit in progress. */
  const baselineKey = (baselineOrder || []).join(">");
  const seeded = useRef(baselineKey);
  useEffect(() => {
    if (baselineKey === seeded.current) return;
    seeded.current = baselineKey;
    setOrder(baselineKey ? baselineKey.split(">") : []);
  }, [baselineKey]);

  const inOrder = useMemo(() => new Set(order), [order]);
  const available = useMemo(
    () => (interfaces || []).filter((w) => !inOrder.has(w.name)),
    [interfaces, inOrder]
  );

  if (!selectedNode) return null;

  const add = (name) => setOrder((o) => Array.from(new Set([...o, name])).slice(0, 9));
  const remove = (name) => setOrder((o) => o.filter((n) => n !== name));
  const move = (from, to) => {
    if (to < 0 || to >= order.length) return;
    setOrder((o) => moved(o, from, to));
  };

  return (
    <div className={`shp-bulk${open ? " shp-bulk--open" : ""}`}>
      <button type="button" className="shp-bulk-head" onClick={() => setOpen((v) => !v)}>
        <span className="shp-bulk-ico"><FiLayers size={16} /></span>
        <span className="shp-bulk-titles">
          <span className="shp-bulk-title">Set a baseline order for every category</span>
          <span className="shp-bulk-sub">
            Order your connections once and apply to all traffic categories at once. Individual
            class rules and QoS failover always take precedence.
          </span>
        </span>
        {open ? <FiChevronUp size={16} className="shp-bulk-chev" /> : <FiChevronDown size={16} className="shp-bulk-chev" />}
      </button>

      {open && (
        <div className="shp-bulk-body">
          <div className="shp-edit-grid">
            {/* Baseline order */}
            <div className="shp-col">
              <div className="shp-col-head">
                <span className="shp-col-title">Baseline order</span>
                <span className="shp-col-hint">top = first choice</span>
              </div>
              <div className={`shp-order${sort.dragging !== null ? " shp-order--sorting" : ""}`} ref={sort.listRef}>
                {order.length === 0 && (
                  <div className="shp-order-empty">No connections chosen yet — add one from the right.</div>
                )}
                {order.map((wan, i) => {
                  return (
                    <div
                      key={wan}
                      {...sort.rowProps(i)}
                      className={`shp-rank${sort.dragging === i ? " shp-rank--drag" : ""}`}
                    >
                      <span className="shp-rank-num" data-sort-handle title="Drag to reorder">{i + 1}</span>
                      <FiWifi size={14} className="shp-rank-ico" />
                      <span className="shp-rank-name"><WanLabel iface={wan} /></span>
                      <div className="shp-rank-moves">
                        <button type="button" className="shp-move" title="Move up" disabled={i === 0} onClick={() => move(i, i - 1)}><FiArrowUp size={12} /></button>
                        <button type="button" className="shp-move" title="Move down" disabled={i === order.length - 1} onClick={() => move(i, i + 1)}><FiArrowDown size={12} /></button>
                      </div>
                      <button type="button" className="shp-rank-rm" title="Remove" onClick={() => remove(wan)}><FiX size={13} /></button>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Available connections */}
            <div className="shp-col">
              <div className="shp-col-head">
                <span className="shp-col-title">Available connections</span>
              </div>
              <div className="shp-avail">
                {available.length === 0 && (
                  <div className="shp-all-added"><FiCheck size={14} /> All connections added</div>
                )}
                {available.map((wan) => {
                  const isUp = wan.state === "up" || wan.state === "UP";
                  return (
                    <button key={wan.name} type="button" className="shp-avail-row" onClick={() => add(wan.name)}>
                      <span className={`shp-dot${isUp ? " shp-dot--up" : ""}`} />
                      <span className="shp-avail-name"><WanLabel iface={wan.name} /></span>
                      {wan.addresses?.[0] && <span className="shp-avail-addr">{wan.addresses[0]}</span>}
                      <FiPlus size={14} className="shp-avail-plus" />
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {bondedCount > 0 && (
            <div className="shp-bulk-warn">
              <FiAlertTriangle size={16} />
              <div>
                <strong>Applying this turns bonding off.</strong>
                {bondedCount === 1
                  ? " 1 category is bonded right now, aggregating traffic across every connection at once."
                  : ` ${bondedCount} categories are bonded right now, aggregating traffic across every connection at once.`}
                {" "}An order only means anything under failover, so applying switches them to it: one connection
                at a time, moving to the next only when it goes offline. Aggregate throughput drops to that of a
                single link. Put any category back on bonding from its own editor below.
              </div>
            </div>
          )}

          <div className="shp-bulk-foot">
            {/* Unlike a single class's list, this one is a draft: it is written
                to every category at once and turns their bonding off, so it
                waits for the one button and its confirmation. */}
            <span className="shp-bulk-note">
              <FiInfo size={12} /> Nothing changes until you apply this order to every category.
              {customizedCount > 0 &&
                ` ${customizedCount} individually-customized categor${customizedCount === 1 ? "y keeps its" : "ies keep their"} own.`}
            </span>
            <button
              className="shp-apply shp-bulk-apply"
              type="button"
              onClick={() => applyBulkOrder(order)}
              disabled={bulkApplying || order.length === 0}
            >
              {bulkApplying ? <><span className="shp-spinner" /> Applying…</> : <><FiLayers size={15} /> Apply to all categories</>}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   ORB WHEEL — a horizontal, scroll-snapping wheel of class "orbs"
   ═══════════════════════════════════════════════════════════════════════════ */

function OrbWheel({ label, items, selectedKey, onSelect, trafficStats, inheritanceMap, activeWanMap, spreadMap, bondedWanMap }) {
  const wanName = useWanName();
  const trackRef = useWheel(selectedKey, items.map((it) => it.key).join(" "));

  const nudge = (dir) => {
    if (trackRef.current) trackRef.current.scrollBy({ left: dir * 240, behavior: "smooth" });
  };

  return (
    <div className="shp-wheelsec">
      <div className="shp-wheel-lbl">{label}</div>
      <div className="shp-wheel-row">
        <button type="button" className="shp-wheel-nav shp-wheel-nav--l" onClick={() => nudge(-1)} aria-label="Scroll left">
          <FiArrowUp size={14} style={{ transform: "rotate(-90deg)" }} />
        </button>
        <div className="shp-wheel" ref={trackRef}>
          {items.map((item) => {
            const ac = classAccent(item);
            const isSel = selectedKey === item.key;
            const pkts = trafficStats?.[item.key];
            const inhInfo = item.isApp ? inheritanceMap?.[item.key] : null;
            const isSpreadOrb = spreadMap ? (spreadMap[item.key] !== false) : false;
            const bonded = bondedWanMap?.[item.key];
            const activeWan = activeWanMap?.[item.key];
            const orbTitle = isSpreadOrb
              ? (bonded?.length ? `${item.label} → bonded across ${bonded.map(wanName).join(", ")}` : `${item.label} → no healthy link to bond`)
              : (activeWan ? `${item.label} → currently on ${wanName(activeWan)}` : item.label);
            return (
              <button
                key={item.key}
                data-orb={item.key}
                type="button"
                className={`shp-orb${isSel ? " shp-orb--on" : ""}`}
                style={{ "--shp-accent": ac }}
                onClick={() => onSelect(isSel ? null : item.key)}
                title={orbTitle}
                aria-pressed={isSel}
              >
                <span className="shp-orb-ring">
                  <span className="shp-orb-ico"><ClassIcon item={item} size={22} /></span>
                  {inhInfo?.inheriting && <span className="shp-orb-link"><FiLink size={9} /></span>}
                </span>
                <span className="shp-orb-name">{item.label}</span>
                {isSpreadOrb ? (
                  bonded?.length ? (
                    <span className="shp-orb-iface" title={`Bonded across ${bonded.map(wanName).join(", ")} right now`}>
                      <FiLayers size={9} /> <span>{bonded.length <= 2 ? bonded.map(wanName).join(" + ") : `${bonded.length} links`}</span>
                    </span>
                  ) : (
                    <span className="shp-orb-iface shp-orb-iface--none">no link</span>
                  )
                ) : activeWan ? (
                  <span className="shp-orb-iface" title={`Carried over ${wanName(activeWan)} right now`}>
                    <FiWifi size={9} /> <span>{wanName(activeWan)}</span>
                  </span>
                ) : (
                  <span className="shp-orb-iface shp-orb-iface--none">no link</span>
                )}
                {typeof pkts === "number" && pkts > 0
                  ? <span className="shp-orb-badge" title={`${pkts.toLocaleString()} packets so far`}>{formatCount(pkts)}</span>
                  : <span className="shp-orb-badge shp-orb-badge--idle">idle</span>}
              </button>
            );
          })}
        </div>
        <button type="button" className="shp-wheel-nav shp-wheel-nav--r" onClick={() => nudge(1)} aria-label="Scroll right">
          <FiArrowDown size={14} style={{ transform: "rotate(-90deg)" }} />
        </button>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN — wheel of traffic classes + blooming editor stage
   ═══════════════════════════════════════════════════════════════════════════ */

export default function TrafficPanels({
  trafficClasses, classFilter, setClassFilter, trafficStats,
  selectedNode, selectedTc, setSelectedTc,
  selectedCurrent,
  activeWanMap,
  spreadMap, bondedWanMap,
  interfaces, loading, availableWans, selectedDesired,
  applyOrder, orderSave, error,
  isInheritingPriority, inheritedFromCategory, inheritanceMap, clearAppOverride,
  applyBulkOrder, bulkApplying, baselineOrder, customizedClasses,
  setClassMode, modeBusy,
}) {
  const wanName = useWanName();
  const [search, setSearch] = useState("");

  const categories = useMemo(() => trafficClasses.filter((c) => !c.isApp), [trafficClasses]);
  const apps = useMemo(() => trafficClasses.filter((c) => c.isApp), [trafficClasses]);

  const q = search.toLowerCase();
  const [filteredCats, filteredApps] = useMemo(() => {
    const matches = (c) => !q || c.label.toLowerCase().includes(q) || c.key.toLowerCase().includes(q);
    return [categories.filter(matches), apps.filter(matches)];
  }, [categories, apps, q]);

  const showCats = classFilter === "all" || classFilter === "category";
  const showApps = classFilter === "all" || classFilter === "app";

  const selectedItem = useMemo(
    () => trafficClasses.find((c) => c.key === selectedTc) || null,
    [trafficClasses, selectedTc]
  );
  const selAccent = selectedItem ? classAccent(selectedItem) : "#6366F1";
  const selInh = selectedItem?.isApp ? inheritanceMap?.[selectedItem.key] : null;
  const noResults = filteredCats.length === 0 && filteredApps.length === 0;

  // Per-WAN live status for the selected class (active carrier / degraded),
  // keyed by interface name. Sourced from /tc_wan_priority/current.
  const selCurrentByName = useMemo(() => {
    const m = {};
    (selectedCurrent || []).forEach((e) => { if (e && e.wan) m[e.wan] = e; });
    return m;
  }, [selectedCurrent]);
  const selectedActiveWan = selectedItem ? activeWanMap?.[selectedItem.key] : null;
  // A bonding class has no single "active" carrier — flows are hashed across
  // every healthy listed connection. Compute that live bonded set so the header
  // and tags stop implying a primary/standby (failover) relationship. An app
  // follows its category's action unless it has its own (spreadMap).
  const selectedSpread = selectedItem ? spreadMap?.[selectedItem.key] !== false : false;
  const bondedLive = useMemo(() => {
    return (selectedDesired || []).filter((w) => {
      const st = selCurrentByName?.[w];
      return st ? (st.enabled !== false && !st.degraded) : true;
    });
  }, [selectedDesired, selCurrentByName]);

  return (
    <div className="shp-wrap">
      {/* How-it-works strip */}
      <div className="shp-explain">
        <FiInfo size={15} className="shp-explain-ico" />
        <div>
          <strong>Pick a traffic type on the wheel, then choose its connections.</strong> Spread
          across these shares its traffic over all of them; Use in this order keeps it on the first
          one that works and moves to the next only when that one goes offline. Categories cover
          broad traffic types; apps let you fine-tune.
        </div>
      </div>

      {/* Toolbar */}
      <div className="shp-toolbar">
        <div className="shp-search">
          <FiSearch size={14} className="shp-search-ico" />
          <input className="shp-search-input" placeholder="Find a category or app…" value={search} onChange={(e) => setSearch(e.target.value)} />
          {search && <button className="shp-search-clear" onClick={() => setSearch("")}><FiX size={13} /></button>}
        </div>
        <div className="shp-pills">
          {[{ key: "all", label: "All" }, { key: "category", label: "Categories" }, { key: "app", label: "Apps" }].map((f) => (
            <button key={f.key} type="button" className={`shp-pill${classFilter === f.key ? " shp-pill--on" : ""}`} onClick={() => setClassFilter(f.key)}>{f.label}</button>
          ))}
        </div>
      </div>

      {error && <div className="shp-error"><FiAlertCircle size={14} /> {error}</div>}

      {/* Bulk baseline-order setter */}
      <BulkOrderEditor
        interfaces={interfaces}
        selectedNode={selectedNode}
        applyBulkOrder={applyBulkOrder}
        bulkApplying={bulkApplying}
        baselineOrder={baselineOrder}
        bondedCount={categories.filter((c) => !customizedClasses?.has(c.key) && spreadMap?.[c.key]).length}
        customizedCount={categories.filter((c) => customizedClasses?.has(c.key)).length}
      />

      {/* Wheels */}
      <div className="shp-wheels">
        {showCats && filteredCats.length > 0 && (
          <OrbWheel label="Traffic categories" items={filteredCats} selectedKey={selectedTc}
            onSelect={setSelectedTc} trafficStats={trafficStats} inheritanceMap={inheritanceMap} activeWanMap={activeWanMap} spreadMap={spreadMap} bondedWanMap={bondedWanMap} />
        )}
        {showApps && filteredApps.length > 0 && (
          <OrbWheel label="Applications" items={filteredApps} selectedKey={selectedTc}
            onSelect={setSelectedTc} trafficStats={trafficStats} inheritanceMap={inheritanceMap} activeWanMap={activeWanMap} spreadMap={spreadMap} bondedWanMap={bondedWanMap} />
        )}
        {noResults && (
          <div className="shp-list-empty"><FiSearch size={22} style={{ opacity: 0.25 }} /><span>No traffic types match “{search}”.</span></div>
        )}
      </div>

      {/* Blooming editor stage */}
      <div className={`shp-stage${selectedItem ? " shp-stage--open" : ""}`}>
        {selectedItem ? (
          <div className="shp-bloom" style={{ "--shp-accent": selAccent }}>
            <div className="shp-bloom-head">
              <span className="shp-bloom-ico" style={{ background: `color-mix(in srgb, ${selAccent} 16%, transparent)`, color: selAccent }}>
                <ClassIcon item={selectedItem} size={20} />
              </span>
              <div className="shp-bloom-titles">
                <span className="shp-bloom-title">{selectedItem.label}</span>
                <span className="shp-bloom-sub">
                  {selectedItem.isApp
                    ? (selInh?.inheriting ? `Currently follows ${selInh.category || selectedItem.category}` : `Custom rule · ${selectedItem.category} app`)
                    : "Traffic category"}
                </span>
              </div>
              {selectedSpread ? (
                bondedLive.length > 0 ? (
                  <span className="shp-bloom-active" title={`${selectedItem.label} is bonded across ${bondedLive.map(wanName).join(", ")} right now`}>
                    <span className="shp-bloom-active-dot" />
                    <span className="shp-bloom-active-lbl">Bonding across</span>
                    <FiLayers size={12} />
                    <strong>{bondedLive.length <= 2 ? bondedLive.map(wanName).join(" + ") : `${bondedLive.length} connections`}</strong>
                  </span>
                ) : (
                  <span className="shp-bloom-active shp-bloom-active--none" title="No healthy connection to bond over">
                    <span className="shp-bloom-active-dot" />
                    <span className="shp-bloom-active-lbl">No healthy link</span>
                  </span>
                )
              ) : selectedActiveWan ? (
                <span className="shp-bloom-active" title={`${selectedItem.label} traffic is on ${wanName(selectedActiveWan)} right now`}>
                  <span className="shp-bloom-active-dot" />
                  <span className="shp-bloom-active-lbl">Now using</span>
                  <FiWifi size={12} />
                  <strong>{wanName(selectedActiveWan)}</strong>
                </span>
              ) : (
                <span className="shp-bloom-active shp-bloom-active--none" title="No online connection for this class">
                  <span className="shp-bloom-active-dot" />
                  <span className="shp-bloom-active-lbl">No online link</span>
                </span>
              )}
              <button type="button" className="shp-bloom-close" onClick={() => setSelectedTc(null)} aria-label="Close"><FiX size={16} /></button>
            </div>
            <PriorityEditor
              accentColor={selAccent}
              selectedClass={selectedItem}
              selectedNode={selectedNode}
              loading={loading}
              interfaces={interfaces}
              availableWans={availableWans}
              selectedDesired={selectedDesired}
              currentByName={selCurrentByName}
              activeWan={selectedActiveWan}
              applyOrder={applyOrder}
              orderSave={orderSave}
              isInheritingPriority={isInheritingPriority}
              inheritedFromCategory={inheritedFromCategory}
              clearAppOverride={clearAppOverride}
              bonded={selectedSpread}
              setClassMode={setClassMode}
              modeBusy={modeBusy}
            />
          </div>
        ) : (
          <div className="shp-stage-hint">
            <FiMousePointer size={16} />
            <span>Pick a traffic type on the wheel to set the order of connections it should use.</span>
          </div>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   HOW TRAFFIC IS RECOGNISED — the classifier's mode, for the whole box
   ═══════════════════════════════════════════════════════════════════════════ */

// Whether the rules on this screen are matched by category, by app, or by an
// app's own rule with its category behind it. Ordered from broad to fine.
const RECOGNISE = [
  { id: "cat", label: "By category", hint: "Traffic is recognised by broad category, such as Realtime or Streaming." },
  { id: "both", label: "By app, then category", hint: "An app's own rule comes first; traffic from any other app follows its category." },
  { id: "app", label: "By app", hint: "Traffic is recognised by the app it comes from, such as Zoom or Netflix." },
];

/** ClassifierSetting is the box's one choice of how traffic is recognised.
    A box without a classifier answers no mode, and then nothing is shown. */
export function ClassifierSetting({ nodeApiUrl, selectedNode }) {
  const [mode, setMode] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { setMode(null); setError(""); }, [selectedNode]);

  // Read again now and then: Ada's Apply and other consoles change it too.
  const load = useCallback(async () => {
    if (!selectedNode || busy) return;
    try {
      const res = await fetchWithTimeout(nodeApiUrl("/classifier/mode"));
      if (!res.ok) return;
      const body = await res.json();
      if (body?.mode) setMode(body.mode);
    } catch { /* unreachable: shown as it was until the box answers */ }
  }, [selectedNode, nodeApiUrl, busy]);
  useVisibleInterval(load, 15000, !!selectedNode);

  const choose = async (next) => {
    if (busy || next === mode) return;
    setBusy(true);
    setError("");
    try {
      const res = await fetchWithTimeout(nodeApiUrl("/classifier/mode"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mode: next }),
      });
      const text = (await res.text()).trim();
      let body = {};
      try { body = JSON.parse(text); } catch { /* plain text from http.Error */ }
      if (!res.ok) throw new Error(body.error || text || `The box refused it (${res.status}).`);
      setMode(body.mode || next);
    } catch (err) {
      setError(err.message || "The box did not take that change.");
    } finally {
      setBusy(false);
    }
  };

  if (!mode) return null;
  const current = RECOGNISE.find((r) => r.id === mode);
  return (
    <div className="shp-recognise">
      <div className="shp-recognise-row">
        <span className="shp-recognise-label">How traffic is recognised</span>
        <div className="shp-mode-seg" role="group" aria-label="How traffic is recognised" data-tour="traffic.classifier">
          {RECOGNISE.map((r) => (
            <button
              key={r.id}
              type="button"
              className={`shp-mode-btn${mode === r.id ? " shp-mode-btn--on" : ""}`}
              aria-pressed={mode === r.id}
              disabled={busy}
              title={r.hint}
              onClick={() => choose(r.id)}
            >
              {r.label}
            </button>
          ))}
        </div>
        {busy && <span className="shp-save-spin" aria-label="Saving" />}
      </div>
      <div className={`shp-recognise-hint${error ? " shp-recognise-hint--err" : ""}`} role={error ? "alert" : undefined}>
        {error ? `That change did not apply: ${error}` : current?.hint}
      </div>
    </div>
  );
}
